import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ContentBlockSchemaRoot } from '@/models/content-block-schema.model';
import { IContentBlockInstance } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';
import { normalizeRootSchema } from '../../helpers/schemaDefaults';
import { GalleryRootEditor } from './GalleryRootEditor';
import { ObjectFieldsEditor } from './ObjectFieldsEditor';
import { InspectorCard } from './SchemaBlockInspectorStyles';

type TSchemaBlockInspector = {
  block: IContentBlockInstance | null;
  schema: ContentBlockSchemaRoot | undefined;
  locales: SupportedLocales[];
  onChange: (next: IContentBlockInstance) => void;
  blockTypeLabel?: string;
};

const SchemaBlockInspector = ({ block, schema, locales, onChange, blockTypeLabel }: TSchemaBlockInspector) => {
  const { t } = useTranslation(['contentPages']);

  const cardTitle = block && blockTypeLabel ? `${t('inspector_title')} — ${blockTypeLabel}` : t('inspector_title');

  if (!block) {
    return (
      <InspectorCard title={t('inspector_title')} size="small">
        <Typography.Text type="secondary">{t('inspector_empty')}</Typography.Text>
      </InspectorCard>
    );
  }

  if (!schema) {
    return (
      <InspectorCard title={cardTitle} size="small">
        <Typography.Text type="secondary">{t('inspector_no_schema', { type: block.type })}</Typography.Text>
      </InspectorCard>
    );
  }

  const root = normalizeRootSchema(schema);

  const patchData = (data: unknown) => {
    onChange({ ...block, data });
  };

  if (root.type === 'array') {
    const arr = Array.isArray(block.data) ? block.data : [];
    return (
      <InspectorCard title={cardTitle} size="small">
        <GalleryRootEditor
          schema={root}
          value={arr}
          onChange={(next) => patchData(next)}
          locales={locales}
          maxItems={root.max_items}
        />
      </InspectorCard>
    );
  }

  const obj =
    block.data && typeof block.data === 'object' && !Array.isArray(block.data)
      ? (block.data as Record<string, unknown>)
      : {};

  if (root.type === 'object' && root.properties) {
    return (
      <InspectorCard title={cardTitle} size="small">
        <ObjectFieldsEditor
          blockType={block.type}
          properties={root.properties}
          value={obj}
          onChange={(next) => patchData(next)}
          locales={locales}
        />
      </InspectorCard>
    );
  }

  return (
    <InspectorCard title={cardTitle} size="small">
      <Typography.Text type="danger">{t('inspector_unsupported_root')}</Typography.Text>
    </InspectorCard>
  );
};

export default SchemaBlockInspector;
